// frontend/src/widgets/MessagesAttentionWidget.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2025
// Contact:      dev@castle-traffic.co.uk
// Created:      2025-12-16
// Updated:      2026-09-08 15:45 BST
//
// Description:
//   Messages Requiring Attention widget.
//   • Will surface conversations needing follow-up:
//       - SLA breaches / stale threads
//       - Failed deliveries
//       - Unread inbound messages
//   • Future: quick actions to jump into the conversation.
//
// Lifecycle Status:
//   Active stub.
//
// Mobile Responsiveness (2026-09-08):
//   - Preserves the existing single render path for desktop and mobile.
//   - Stacks the title/description and severity selector on narrow screens.
//   - Makes the severity selector full-width and touch-friendly on mobile.
//   - Uses natural widget height on mobile while retaining desktop fill behaviour.
//   - Allows planned-focus and stub text to wrap safely without horizontal overflow.
//   - No API, data-loading or conversation behaviour has been introduced.
//
// Operational Notes:
//   - This widget remains a presentation-only stub.
//   - It does not currently query alerts, messages or conversation data.
//   - The severity selector remains non-functional until the widget is wired to
//     its future data source.

import React from "react";

import { useTheme } from "../context/ThemeContext.jsx";

const MessagesAttentionWidget = () => {
  const { theme } = useTheme() || {};
  const accent =
    theme?.accent_color ||
    "#ff7a00";

  return (
    <div className="flex h-auto min-h-0 w-full flex-col overflow-visible md:h-full md:overflow-hidden">
      {/* Header */}
      <div className="mb-3 flex flex-col gap-3 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0 flex-1">
          <h3
            className="m-0 break-words text-base font-semibold"
            style={{
              color: accent,
              overflowWrap:
                "anywhere",
            }}
          >
            Messages Requiring Attention
          </h3>

          <p
            className="mt-1 mb-0 break-words text-xs leading-5 text-gray-600"
            style={{
              overflowWrap:
                "anywhere",
            }}
          >
            Highlights conversations that may need follow-up.
          </p>
        </div>

        <div className="w-full shrink-0 md:w-auto">
          <label
            htmlFor="messages-attention-severity"
            className="sr-only"
          >
            Message attention severity
          </label>

          <select
            id="messages-attention-severity"
            className="min-h-[44px] w-full rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 md:min-h-0 md:w-auto md:px-2 md:py-1 md:text-xs"
            style={{
              "--tw-ring-color":
                accent,
            }}
            defaultValue="all"
          >
            <option value="all">
              All severities
            </option>

            <option value="critical">
              Critical only
            </option>

            <option value="warning">
              Warnings
            </option>
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="min-h-0 flex-1 rounded-lg border border-dashed border-gray-300 bg-gray-50 p-3 text-xs leading-5 text-gray-700 md:p-4">
        <p className="mt-0 mb-2 font-semibold">
          Planned focus:
        </p>

        <ul className="m-0 list-disc space-y-1 pl-5">
          <li className="break-words">
            Recently received inbound messages with no reply.
          </li>

          <li className="break-words">
            Conversations older than X hours with pending status.
          </li>

          <li className="break-words">
            Messages with delivery failures / bounced emails.
          </li>
        </ul>

        <p
          className="mt-4 mb-0 break-words italic text-gray-500"
          style={{
            overflowWrap:
              "anywhere",
          }}
        >
          Stub only – will be wired to alerts / conversations tables in a later
          pass.
        </p>
      </div>
    </div>
  );
};

export default MessagesAttentionWidget;
